import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, AlertCircle, Zap } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API } from "@/config/api";

const RequestHelp_v2 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    bill_type: "",
    amount_requested: "",
    due_date: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setWarning(null);

    try {
      const response = await axios.post(`${API}/member/help-request`, {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested)
      }, {
        withCredentials: true
      });
      
      if (response.data.warning) {
        setWarning(response.data.warning);
      }
      
      toast.success('Request submitted successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto max-w-2xl pt-16">
        <Button 
          data-testid="back-to-dashboard-btn"
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12">
          <h1 className="text-4xl font-heading font-bold mb-4 text-white" data-testid="request-help-title">
            Request Help with a Bill
          </h1>
          <p className="text-slate-400 mb-8">
            Tell us about the bill you need help with. We'll review your request and get back to you within 24 hours.
          </p>

          {warning && (
            <Alert className="mb-6 border-amber-500/50 bg-amber-500/10" data-testid="warning-alert">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-amber-300">
                {warning}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="bill_type" className="text-white" data-testid="bill-type-label">Bill Type</Label>
              <Select 
                value={formData.bill_type} 
                onValueChange={(value) => setFormData({...formData, bill_type: value})}
                required
              >
                <SelectTrigger 
                  data-testid="bill-type-select" 
                  className="mt-2 bg-slate-900/50 border-slate-600 text-white focus:border-yellow-400"
                >
                  <SelectValue placeholder="Select bill type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="rent" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Rent/Mortgage</SelectItem>
                  <SelectItem value="electric" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Electric</SelectItem>
                  <SelectItem value="gas" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Gas</SelectItem>
                  <SelectItem value="internet" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Internet</SelectItem>
                  <SelectItem value="phone" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Phone</SelectItem>
                  <SelectItem value="car" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Car Payment/Insurance</SelectItem>
                  <SelectItem value="other" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Other Essential Bill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount" className="text-white" data-testid="amount-label">Amount Needed</Label>
              <Input
                id="amount"
                data-testid="amount-input"
                type="number"
                step="0.01"
                placeholder="150.00"
                value={formData.amount_requested}
                onChange={(e) => setFormData({...formData, amount_requested: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
                required
              />
            </div>

            <div>
              <Label htmlFor="due_date" className="text-white" data-testid="due-date-label">Bill Due Date</Label>
              <Input
                id="due_date"
                data-testid="due-date-input"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white focus:border-yellow-400 [color-scheme:dark]"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-white" data-testid="description-label">
                What happened this month?
              </Label>
              <Textarea
                id="description"
                data-testid="description-textarea"
                placeholder="Please explain your situation... (e.g., unexpected car repair, reduced hours at work)"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400 min-h-[120px]"
                required
              />
            </div>

            <Button
              data-testid="submit-request-btn"
              type="submit"
              className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full w-full py-6 text-lg font-bold"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Your request will be reviewed by our team. We'll notify you of the decision via email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RequestHelp_v2;
